import React, { useCallback, useEffect, useState } from 'react';
import BackendPage from '../../components/page/BackendPage';
import { Tabs } from '@mantine/core';
import UpgradePathsList from '../../components/upgrades/UpgradePathsList';
import UpgradePromptsList from '../../components/upgrades/UpgradePromptsList';
import { useUpgradeService } from '../../services/UpgradeService';
import type { UpgradePathDiscountListFragment } from '../../graphql/graphql';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';

// Main admin page for managing the upgrade upsell system.
// Provides two tabs: one for configuring upgrade paths (discount rules) and one for prompts (UI elements).
// This separation allows marketing teams to manage the business logic (paths) separately from the messaging (prompts).
export default function UpgradesPage() {
	const upgradeService = useUpgradeService();
	const [paths, setPaths] = useState<UpgradePathDiscountListFragment[]>([]);
	const [isLoadingPaths, setIsLoadingPaths] = useState(true);

	const fetchPaths = useCallback(async () => {
		setIsLoadingPaths(true);
		try {
			const result = await upgradeService.getAllPaths().response;
			setPaths(result);
		} catch (error) {
			NotificationUtils.showError(error as Error, 'Failed to load upgrade paths');
			setPaths([]);
		} finally {
			setIsLoadingPaths(false);
		}
	}, [upgradeService]);

	const handleCreated = useCallback(() => fetchPaths(), [fetchPaths]);

	const handleUpdated = useCallback(() => fetchPaths(), [fetchPaths]);

	useEffect(() => {
		fetchPaths();
	}, [fetchPaths]);

	return (
		<BackendPage title="Upgrade Paths & Prompts" description="Manage upgrade rules, discounts and prompt copies.">
			<Tabs defaultValue="paths">
				<Tabs.List>
					<Tabs.Tab value="paths">Upgrade Paths</Tabs.Tab>
					<Tabs.Tab value="prompts">Upgrade Prompts</Tabs.Tab>
				</Tabs.List>

				<Tabs.Panel value="paths" pt="md">
					<UpgradePathsList
						paths={paths}
						isLoadingPaths={isLoadingPaths}
						onPathsChanged={fetchPaths}
						onPathCreated={handleCreated}
						onPathUpdated={handleUpdated}
					/>
				</Tabs.Panel>

				<Tabs.Panel value="prompts" pt="md">
					<UpgradePromptsList paths={paths} isLoadingPaths={isLoadingPaths} />
				</Tabs.Panel>
			</Tabs>
		</BackendPage>
	);
}
